import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { BuddyAvatar, BUDDY_ROSTER } from './BuddyRoster';
import { Users } from 'lucide-react';

/**
 * BuddyChatGreeting — replaces the empty-state hero on the chat home with a
 * personal greeting from the user's selected buddy. Shows on /home before the
 * first message of a new session.
 */
export default function BuddyChatGreeting({ onPickFriend }) {
  const { user } = useAuth();
  const [shown, setShown] = useState(false);
  const buddyId = user?.preferred_buddy || 'chirpy';
  const buddy = BUDDY_ROSTER[buddyId] || BUDDY_ROSTER.saathi;
  const firstName = (user?.first_name || user?.name?.split(' ')[0] || 'friend');

  useEffect(() => { setShown(true); }, []);

  // Iter 60g (Jul 2026) — Lock the greeting variant per mount. Previously
  // `Math.random()` ran on every render, so ANY parent re-render (e.g. the
  // student typing a letter in the message box below) would flip the
  // greeting mid-sentence. Freezing it inside `useMemo` keyed on the
  // user's buddy + name means it stays stable while they're on the page.
  const greeting = useMemo(() => {
    const options = [
      `Hi ${firstName}! I'm ${buddy.name}. \uD83D\uDC4B What do you want to learn today?`,
      `Hey ${firstName}! ${buddy.name} here. Pick a topic above and we'll dive in together.`,
      `${firstName}! Good to see you. What's the topic on your mind today?`,
    ];
    return options[Math.floor(Math.random() * options.length)];
  }, [firstName, buddy.name]);

  return (
    <motion.div
      data-testid="buddy-chat-greeting"
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: shown ? 1 : 0, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="flex items-end gap-2 sm:gap-3 max-w-xl mx-auto"
    >
      <div className="flex-shrink-0">
        <motion.div
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full ring-4 ring-white shadow-xl bg-gradient-to-br from-amber-100 to-rose-100 overflow-hidden"
        >
          <BuddyAvatar buddyId={buddyId} size={80} mood="happy" />
        </motion.div>
      </div>
      <div className="relative bg-white rounded-3xl rounded-bl-sm border border-amber-100 shadow-sm px-3 py-2.5 sm:px-4 sm:py-3 max-w-[240px] sm:max-w-[420px]">
        <p className="text-[9px] sm:text-[10px] uppercase tracking-wider font-heading text-amber-600 mb-0.5 sm:mb-1">{buddy.name} &middot; {buddy.tagline}</p>
        <p className="text-sm sm:text-[15px] font-body text-slate-800 leading-snug sm:leading-relaxed">{greeting}</p>
        <button
          onClick={onPickFriend}
          data-testid="meet-friends-link"
          className="mt-1 sm:mt-1.5 inline-flex items-center gap-1 text-[11px] sm:text-xs font-heading text-emerald-600 hover:text-emerald-700"
        >
          <Users className="w-3 h-3" /> Meet all my friends
        </button>
        <span className="absolute -left-1.5 bottom-3 w-3 h-3 bg-white border-l border-b border-amber-100 rotate-45" />
      </div>
    </motion.div>
  );
}
